import { pipe } from 'fp-ts/lib/function';
import { ColorType } from 'highcharts';
import moment from 'moment';
import { WidgetType } from 'domain/entity/widget';
import ChartData, {
    Resolution,
    ChartAxisType,
    DateChartValue,
    ChartAxis,
    SerieMetaStep,
    SeriesType,
    ChartSeriesType,
    SerieBorder,
    ChartSerieMetaBorder,
    DateTimeChartAxis,
    NumericChartValue,
} from 'domain/entity/widgets/ChartData';
import {
    JsonColor,
    JsonWidgetData,
    mapColor,
    mapDataAvailability,
    mapDate,
    mapDescription,
} from 'adapter/out/client/widgets/data/mappers/common';

import { Color } from 'domain/entity/widgetData';

export const MAIN_X_AXIS_KEY = 'MAIN';
export const COMPARISON_KEY = 'COMPARISON';
const ONE_DAY_IN_MILIS = 86400000;

export type JsonSerie = {
    type: SeriesType;
    values: JsonChartValues[];
    color: JsonColor;
    ['axis_y']: string;
    ['axis_x']: string;
    title: string;
    meta: JsonMeta;
    stack: string | null;
    ['fill_pattern']: SerieBorder;
    ['point_less']: boolean;
    resolution: Resolution;
};

type JsonMeta = {
    groups?: { key: string; name: string; color: string }[];
    color?: string;
    area?: boolean;
    step?: SerieMetaStep;
    border?: SerieBorder;
};

export type JsonSerieMeta = {
    area: boolean;
    step: SerieMetaStep;
    border: SerieBorder;
};

export type JsonChartValues = { x: string; y: string; t?: string; group?: string };

export type JsonChartAxisMeta = JsonCategorizedChartAxisMeta | JsonDateTimeChartAxisMeta | JsonNumericChartAxisMeta | Record<string, never>;

export type JsonCategorizedChartAxisMeta = {
    categories: string[];
};
export type JsonDateTimeChartAxisMeta = {
    timezone: string;
    resolution: Resolution;
    from: string;
    to: string;
};
export type JsonNumericChartAxisMeta = {
    scaling?: string;
    min?: number;
    max?: number;
};

export type JsonChartAxis = {
    key: string;
    title: string;
    units: string;
    type: string;
    meta: JsonChartAxisMeta;
};

export type JsonChartData = JsonWidgetData & {
    data: {
        series: JsonSerie[];
        ['axes_x']: JsonChartAxis[];
        ['axes_y']: JsonChartAxis[];
    };
};

const mapAxis = (axis: JsonChartAxis): ChartAxis => {
    const { key, title, units, type, meta } = axis;

    switch (type) {
        case ChartAxisType.CATEGORIZED: {
            const categorizedMeta = meta as JsonCategorizedChartAxisMeta;
            return { key, title, units, type, meta: { categories: categorizedMeta.categories } };
        }
        case ChartAxisType.NUMERIC: {
            const numericMeta = meta as JsonNumericChartAxisMeta;
            return {
                key,
                title,
                units,
                type,
                meta:
                    numericMeta && (numericMeta.scaling || numericMeta.min !== undefined || numericMeta.max !== undefined)
                        ? {
                              scaling: numericMeta.scaling,
                              min: numericMeta.min,
                              max: numericMeta.max,
                          }
                        : {},
            };
        }
        case ChartAxisType.DATETIME:
        default: {
            const datetimeMeta = meta as JsonDateTimeChartAxisMeta;
            return {
                key,
                title,
                units,
                type: type as ChartAxisType.DATETIME,
                meta: datetimeMeta
                    ? {
                          timezone: datetimeMeta.timezone,
                          resolution: datetimeMeta.resolution,
                          from: mapDate(datetimeMeta.from),
                          to: mapDate(datetimeMeta.to),
                      }
                    : {},
            };
        }
    }
};

export const mapSeriesType = (type: SeriesType): ChartSeriesType => {
    switch (type) {
        case SeriesType.LINE:
            return ChartSeriesType.LINE;
        case SeriesType.TOOLTIP:
            return ChartSeriesType.TOOLTIP;
        case SeriesType.GROUPED_LINE:
            return ChartSeriesType.GROUPED_LINE;
        case SeriesType.GROUPED_BAR:
            return ChartSeriesType.GROUPED_BAR;
        case SeriesType.SCATTER:
            return ChartSeriesType.SCATTER;
        case SeriesType.BAR:
        case SeriesType.STACKED_BAR:
        default:
            return ChartSeriesType.COLUMN;
    }
};

export const getPatternSeries = (color: string, serieBorder: SerieBorder, type: ChartSeriesType): ColorType => {
    if (type === ChartSeriesType.LINE || type === ChartSeriesType.SCATTER) return color;
    switch (serieBorder) {
        case SerieBorder.DASHED:
            return {
                pattern: {
                    color,
                    path: {
                        d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
                        strokeWidth: 3,
                    },
                    width: 10,
                    height: 10,
                    opacity: 1,
                },
            };
        case SerieBorder.DOTTED:
            return {
                pattern: {
                    color,
                    path: {
                        d: 'M 0 5 L 10 5',
                        strokeWidth: 3,
                    },
                    width: 10,
                    height: 10,
                    opacity: 1,
                },
            };
        case SerieBorder.SOLID:
        default:
            return color;
    }
};

export const mapBorderSerie = (border?: SerieBorder): ChartSerieMetaBorder => {
    switch (border) {
        case SerieBorder.DASHED:
            return ChartSerieMetaBorder.DASH;
        case SerieBorder.DOTTED:
            return ChartSerieMetaBorder.DOT;
        case SerieBorder.SOLID:
        default:
            return ChartSerieMetaBorder.SOLID;
    }
};

export const mapSerieMeta = (meta: JsonMeta, color: Color, isCompared: boolean) => {
    const getFillColorMeta = (
        c: Color,
        isComp: boolean
    ): {
        pattern: {
            color: string;
            path?: {
                d: string;
                strokeWidth: number;
            };
            width?: number;
            height?: number;
        };
    } => {
        const hexToRGBA = (hex: string, opacity: number): string => {
            const hexValue = hex.replace('#', '');
            const r = parseInt(hexValue.substring(0, 2), 16);
            const g = parseInt(hexValue.substring(2, 4), 16);
            const b = parseInt(hexValue.substring(4, 6), 16);
            return `rgba(${r},${g},${b},${opacity})`;
        };
        if (!isComp) {
            return {
                pattern: {
                    color: hexToRGBA(c.byDefault, 0.5),
                },
            };
        }
        return {
            pattern: {
                color: hexToRGBA(c.byDefault, 0.5),
                path: {
                    d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
                    strokeWidth: 3,
                },
                width: 10,
                height: 10,
            },
        };
    };
    if (Object.keys(meta).length === 0) return {};
    if ('color' in meta) return { color: meta.color };
    if ('groups' in meta) return { groups: meta.groups };
    return pipe(meta, (chartSerieMeta) => ({
        area: chartSerieMeta.area,
        step: chartSerieMeta.step !== SerieMetaStep.NONE ? chartSerieMeta.step : undefined,
        fillColor: chartSerieMeta.area ? getFillColorMeta(color, isCompared) : undefined,
        border: mapBorderSerie(chartSerieMeta.border),
    }));
};

export const isComparison = (axisX: string, stack: string | null) => isAxisXComparison(axisX) || stack?.indexOf(COMPARISON_KEY) === 0;
export const isAxisXComparison = (axisX: string) => axisX.indexOf(COMPARISON_KEY) === 0;

export const ChartMapper = (json: JsonChartData): ChartData => {
    const parsedXAxis = mapAxes(json.data.axes_x);
    const parsedYAxes = mapAxes(json.data.axes_y);

    const parsedSeries = json.data.series.map((serie) => {
        let parsedValues: DateChartValue[] | NumericChartValue[];

        const axisTypeOfSerie = json.data.axes_x.find((axis: JsonChartAxis) => axis.key === serie.axis_x)?.type as ChartAxisType;

        if (axisTypeOfSerie === ChartAxisType.DATETIME) {
            parsedValues = fillWithNullsIfLineAndNotBasic(serie, json, parsedXAxis);
        } else if (axisTypeOfSerie === ChartAxisType.CATEGORIZED || axisTypeOfSerie === ChartAxisType.NUMERIC) {
            parsedValues = mapCategorizedNumericValues(serie.values);
        } else {
            parsedValues = pipe(mapDatetimeValues(serie.values), (values) =>
                isComparison(serie.axis_x, serie.stack) ? offsetComparisonDatestoMain(values, parsedXAxis) : values
            );
        }

        return pipe(mapSeriesType(serie.type), (type) => ({
            type,
            values: parsedValues,
            color: mapColor(serie.color),
            axisY: serie.axis_y,
            axisX: serie.axis_x,
            title: serie.title,
            meta: mapSerieMeta(serie.meta, mapColor(serie.color), isComparison(serie.axis_x, serie.stack)),
            stack: serie.stack,
            fillPattern: serie.fill_pattern,
            pointLess: serie.point_less,
            resolution: serie.resolution,
        }));
    });
    return new ChartData(
        WidgetType[json.type],
        parsedSeries,
        parsedXAxis,
        parsedYAxes,
        mapDataAvailability(json.data_availability),
        mapDescription(json.description)
    );
};

const fillWithNullsIfLineAndNotBasic = (serie: JsonSerie, json: JsonChartData, parsedXAxis: ChartAxis[]) =>
    (serie.type === SeriesType.LINE || serie.type === SeriesType.GROUPED_LINE || serie.type === SeriesType.BAR) &&
    serie.resolution !== Resolution.B
        ? pipe(
              json.data.axes_x.find((axis: JsonChartAxis) => axis.key === serie.axis_x)?.meta as JsonDateTimeChartAxisMeta,
              (meta) => mapValuesAndFillNulls(serie.values, meta.resolution, meta.from, meta.to),
              (values) => (isComparison(serie.axis_x, serie.stack) ? offsetComparisonDatestoMain(values, parsedXAxis) : values)
          )
        : pipe(mapDatetimeValues(serie.values), (values) =>
              isComparison(serie.axis_x, serie.stack) ? offsetComparisonDatestoMain(values, parsedXAxis) : values
          );

const mapCategorizedNumericValues = (values: JsonChartValues[]): NumericChartValue[] =>
    values.map((mappedValue) => ({
        x: parseFloat(mappedValue.x),
        y: mappedValue.y !== null ? parseFloat(mappedValue.y) : null,
        t: mappedValue.t ? mapDate(mappedValue.t) : undefined,
        group: mappedValue.group,
    }));

const mapDatetimeValues = (values: JsonChartValues[]): DateChartValue[] =>
    values.map((mappedValue) => ({
        x: mapDate(mappedValue.x),
        y: mappedValue.y !== null ? parseFloat(mappedValue.y) : null,
        group: mappedValue.group,
    }));

const mapAxes = (axes: JsonChartAxis[]): ChartAxis[] => axes.map(mapAxis);

export const populateSerieChartValue = (
    nextDateByResolution: Date,
    finalDate: Date,
    valuesDictionary: Record<string, JsonChartValues>,
    resolution: Resolution,
    yDefaultValue: number | null = null
): DateChartValue[] => {
    let nextDate = new Date(nextDateByResolution);
    const serie = [];
    let groupValue;
    while (nextDate.getTime() <= finalDate.getTime()) {
        let yValue = yDefaultValue;
        groupValue = undefined;
        if (nextDate.getTime() in valuesDictionary) {
            yValue = parseFloat(valuesDictionary[nextDate.getTime()].y);
            groupValue = valuesDictionary[nextDate.getTime()].group;
        }
        serie.push({ x: nextDate, y: yValue, group: groupValue });
        nextDate = getNextLogicalDate(nextDate, resolution);
    }
    return serie;
};

export const mapValuesAndFillNulls = (values: JsonChartValues[], resolution: Resolution, from: string, to: string): DateChartValue[] => {
    const nextDateByResolution = mapDate(resolution === Resolution.W && values.length > 0 ? values[0].x : from);
    const finalDate = mapDate(to);
    const valuesDictionary: Record<string, JsonChartValues> = {};

    values.forEach((mappedValue) => {
        valuesDictionary[`${mapDate(mappedValue.x).getTime()}`] = mappedValue;
    });

    return populateSerieChartValue(nextDateByResolution, finalDate, valuesDictionary, resolution);
};

export const getNextLogicalDate = (date: Date, resolution: Resolution): Date => {
    const newDate = new Date(date);
    switch (resolution) {
        case Resolution.D: {
            return new Date(newDate.setUTCDate(date.getUTCDate() + 1));
        }
        case Resolution.FM: {
            return new Date(newDate.setUTCMinutes(date.getUTCMinutes() + 5));
        }
        case Resolution.H: {
            return new Date(newDate.setUTCHours(date.getUTCHours() + 1));
        }
        case Resolution.HH: {
            return new Date(newDate.setUTCMinutes(date.getUTCMinutes() + 30));
        }
        case Resolution.M: {
            let month = (date.getUTCMonth() + 2).toString().padStart(2, '0');
            let year = date.getUTCFullYear();
            const day = '01';
            if (month === '13') {
                month = '01';
                year = date.getUTCFullYear() + 1;
            }
            return new Date(Date.parse(`${year}-${month}-${day}T00:00:00+00:00`));
        }
        case Resolution.QH: {
            return new Date(newDate.setUTCMinutes(date.getUTCMinutes() + 15));
        }
        case Resolution.TM: {
            return new Date(newDate.setUTCMinutes(date.getUTCMinutes() + 10));
        }
        // TODO: Handle 'B' as Resolution is not managed
        case Resolution.W:
        default: {
            return new Date(newDate.setUTCDate(date.getUTCDate() + 7));
        }
    }
};

export const offsetComparisonDatestoMain = (values: DateChartValue[], axesX: ChartAxis[]): DateChartValue[] => {
    const mainChartAxis = axesX.find((axis) => axis.key === MAIN_X_AXIS_KEY);
    const comparisonChartAxis = axesX.find((axis) => isAxisXComparison(axis.key)) as DateTimeChartAxis;
    if (
        mainChartAxis &&
        mainChartAxis.type === ChartAxisType.DATETIME &&
        comparisonChartAxis &&
        comparisonChartAxis.type === ChartAxisType.DATETIME
    ) {
        const mainFromInMilis = mainChartAxis.meta.from.getTime();
        const comparisonFromInMilis = comparisonChartAxis.meta.from.getTime();
        let offset = 0;
        switch (mainChartAxis.meta.resolution) {
            case Resolution.M:
                offset =
                    moment(mainFromInMilis).set('date', 1).toDate().getTime() -
                    moment(comparisonFromInMilis).set('date', 1).toDate().getTime();
                return values.map(
                    (singleValue: DateChartValue): DateChartValue => {
                        const leapYearCompensatedOffset = adjustOffsetToLeapYear(singleValue.x.getTime(), offset);
                        return {
                            x: new Date(adjustToFirstOfMonth(singleValue.x.getTime() + leapYearCompensatedOffset)),
                            y: singleValue.y,
                            unmodifiedX: singleValue.x,
                            group: singleValue.group,
                        };
                    }
                );
            case Resolution.W:
                offset =
                    moment(mainFromInMilis).set('weekday', 1).toDate().getTime() -
                    moment(comparisonFromInMilis).set('weekday', 1).toDate().getTime();

                return values.map(
                    (singleValue: DateChartValue): DateChartValue => ({
                        x: new Date(singleValue.x.getTime() + offset),
                        y: singleValue.y,
                        unmodifiedX: singleValue.x,
                        group: singleValue.group,
                    })
                );
            default:
                offset = mainFromInMilis - comparisonFromInMilis;
                return values.map(
                    (singleValue: DateChartValue): DateChartValue => ({
                        x: new Date(singleValue.x.getTime() + offset),
                        y: singleValue.y,
                        unmodifiedX: singleValue.x,
                        group: singleValue.group,
                    })
                );
        }
    }

    return values.map(
        (singleValue: DateChartValue): DateChartValue => ({
            x: singleValue.x,
            y: singleValue.y,
            group: singleValue.group,
        })
    );
};

const adjustOffsetToLeapYear = (value: number, dateOffset: number): number => {
    const valueAsMoment = moment(value);
    let compensatedOffset = dateOffset;

    if (valueAsMoment.isLeapYear() && valueAsMoment.month() > 1) {
        compensatedOffset = dateOffset - ONE_DAY_IN_MILIS;
    }
    if (
        moment(value + dateOffset).isLeapYear() &&
        moment(value + dateOffset)
            .utc(true)
            .month() > 1
    ) {
        compensatedOffset = dateOffset + ONE_DAY_IN_MILIS;
    }

    return compensatedOffset;
};

const adjustToFirstOfMonth = (timestamp: number): number => {
    const adjustedTimestamp = 0;
    if (new Date(timestamp).getDate() >= 28) {
        return moment(timestamp + adjustedTimestamp)
            .utc(true)
            .set('date', 1)
            .add(1, 'month')
            .set('hours', 0)
            .set('minutes', 0)
            .set('seconds', 0)
            .valueOf();
    }

    return moment(timestamp + adjustedTimestamp)
        .utc(true)
        .set('date', 1)
        .set('hours', 0)
        .set('minutes', 0)
        .set('seconds', 0)
        .valueOf();
};
